import React, { useMemo } from "react";

import * as R from "ramda";

import { Dropdown as BSDropdown } from "react-bootstrap";

import { typedReactMemo } from "../../utils/types";

import "./DropdownToggle.scss";
import { DropdownToggleType } from "./Dropdown";
import { OverlayTrigger } from "../../Components";
import { Placement } from "../OverlayTrigger";

const CaretIcon = () => (
  <svg
    className="caret-icon"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 18 18"
  >
    <path
      fill="currentColor"
      d="M4.75,7.29c-.25-.33-.02-.81,.4-.81h7.72c.42,0,.65,.48,.4,.81l-.15,.19-3.75,3.88c-.2,.2-.52,.2-.72,0l-3.75-3.88-.15-.19Z"
    />
  </svg>
);

export type DropdownOption<T extends string = string> =
  | T
  | {
      label?: string;
      value: T;
    };

interface DropdownToggleProps<T extends string> {
  type?: DropdownToggleType;
  design?: "primary" | "secondary";
  background?: "light" | "dark";
  className?: string;
  label?: string | JSX.Element;
  value?: T;
  options: readonly DropdownOption<T>[];
  leadingIcon?: React.ReactElement;
  size?: "sm" | "lg";
  disabled?: boolean;
  placeholder?: string;
  width?: number;
  overlay?: React.ReactElement;
  overlayPlacement?: Placement;
  [passthroughProp: string]: any;
}

export const DropdownToggle = typedReactMemo(
  <T extends string>({
    type = DropdownToggleType.FILLED,
    design = "primary",
    background = "light",
    className,
    label,
    value,
    options,
    leadingIcon,
    size = "lg",
    disabled,
    placeholder,
    width,
    overlay,
    overlayPlacement = "right center",
  }: DropdownToggleProps<T>): JSX.Element => {
    const resolvedClassName = useMemo(() => {
      let classes = ["cl-dropdown-toggle", type, design, background, size, className];
      if (leadingIcon) {
        classes.push("has-leading-icon");
      }
      if (disabled) {
        classes.push("disabled");
      }

      return classes.join(" ");
    }, [type, design, background, size, className, leadingIcon, disabled]);

    const selectedOption = useMemo(
      () =>
        R.find(option => (typeof option === "string" ? option : option.value) === value, options) ||
        value,
      [value, options]
    );
    const title = useMemo(
      () => (
        <span className="dt-text">
          {label && (
            <span className="dt-label">{typeof label === "string" ? `${label}: ` : label}</span>
          )}
          {selectedOption ? (
            <span className="dt-value">{`${
              typeof selectedOption === "string"
                ? selectedOption
                : selectedOption?.label || selectedOption?.value
            }`}</span>
          ) : (
            <span className="dt-placeholder">{placeholder || ""}</span>
          )}
        </span>
      ),
      [label, selectedOption, placeholder]
    );

    const dropdownToggle = (
      <BSDropdown.Toggle className={resolvedClassName} disabled={disabled}>
        {leadingIcon && <div className="dt-icon">{leadingIcon}</div>}
        {title}
        <CaretIcon />
      </BSDropdown.Toggle>
    );

    return overlay ? (
      <OverlayTrigger placement={overlayPlacement} overlay={overlay}>
        {dropdownToggle}
      </OverlayTrigger>
    ) : (
      dropdownToggle
    );
  }
);
